/** Mandatory password change when the account has a temporary password. */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, KeyRound } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { changePassword } from '@/services/auth/authService';
import { formatApiError } from '@/services/api/client';
import { useAuth } from '@/app/providers/AuthProvider';
import { USERS } from '@/constants/testIds';

export function ChangePasswordGate() {
        const { user, refreshSession } = useAuth();
        const [current, setCurrent] = useState('');
        const [next, setNext] = useState('');

        const mut = useMutation({
                mutationFn: () => changePassword({ current_password: current, new_password: next }),
                onSuccess: async () => {
                        toast.success('Password aggiornata');
                        setCurrent(''); setNext('');
                        await refreshSession();
                },
                onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
        });

        const open = !!user?.must_change_password;

        return (
                <Dialog open={open}>
                        <DialogContent data-testid={USERS.changePasswordDialog}
                                className="border-slate-800 bg-slate-950 text-slate-100 [&>button]:hidden">
                                <DialogHeader>
                                        <DialogTitle className="flex items-center gap-2"><KeyRound size={18} className="text-emerald-300" /> Cambia password</DialogTitle>
                                        <DialogDescription className="text-slate-400">Stai usando una password temporanea. Impostane una nuova per continuare.</DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                        <div>
                                                <Label className="text-xs text-slate-400">Password attuale (temporanea)</Label>
                                                <Input data-testid={USERS.currentPasswordInput} type="password" value={current}
                                                        onChange={(e) => setCurrent(e.target.value)}
                                                        className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                                        </div>
                                        <div>
                                                <Label className="text-xs text-slate-400">Nuova password (min 8 caratteri)</Label>
                                                <Input data-testid={USERS.newPasswordInput} type="password" value={next}
                                                        onChange={(e) => setNext(e.target.value)}
                                                        className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                                        </div>
                                </div>
                                <DialogFooter>
                                        <Button data-testid={USERS.changePasswordSubmit} onClick={() => mut.mutate()}
                                                disabled={mut.isPending || current.length === 0 || next.length < 8}
                                                className="bg-emerald-400 text-slate-950 hover:bg-emerald-300">
                                                {mut.isPending && <Loader2 size={15} className="mr-1 animate-spin" />} Aggiorna password
                                        </Button>
                                </DialogFooter>
                        </DialogContent>
                </Dialog>
        );
}

export default ChangePasswordGate;
